import { Check } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { shortcutLabel } from '../../lib/keys';

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export function MenuContent({
  className,
  children,
  align = 'end',
  ...rest
}: ComponentProps<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align={align}
        sideOffset={6}
        collisionPadding={8}
        className={cn(
          'z-50 min-w-48 rounded-xl border border-slate-200 bg-white p-1 shadow-float animate-pop-in dark:border-slate-700 dark:bg-slate-900',
          className,
        )}
        {...rest}
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

interface MenuItemProps extends ComponentProps<typeof DropdownMenu.Item> {
  icon?: ReactNode;
  shortcut?: string;
  tone?: 'default' | 'danger';
}

export function MenuItem({
  icon,
  shortcut,
  tone = 'default',
  className,
  children,
  ...rest
}: MenuItemProps) {
  return (
    <DropdownMenu.Item
      className={cn(
        'flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        tone === 'danger'
          ? 'text-red-600 data-[highlighted]:bg-red-50 dark:text-red-400 dark:data-[highlighted]:bg-red-950/40'
          : 'text-slate-700 data-[highlighted]:bg-slate-100 dark:text-slate-200 dark:data-[highlighted]:bg-slate-800',
        className,
      )}
      {...rest}
    >
      {icon && <span className="flex size-4 items-center justify-center opacity-80">{icon}</span>}
      <span className="flex-1">{children}</span>
      {shortcut && <span className="text-xs text-slate-400">{shortcutLabel(shortcut)}</span>}
    </DropdownMenu.Item>
  );
}

/** A menu item that toggles a setting, with a check mark when on. */
export function MenuCheckboxItem({
  className,
  children,
  ...rest
}: ComponentProps<typeof DropdownMenu.CheckboxItem>) {
  return (
    <DropdownMenu.CheckboxItem
      className={cn(
        'flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-slate-700 outline-none data-[highlighted]:bg-slate-100 dark:text-slate-200 dark:data-[highlighted]:bg-slate-800',
        className,
      )}
      {...rest}
    >
      <span className="flex size-4 items-center justify-center">
        <DropdownMenu.ItemIndicator>
          <Check className="size-4 text-brand-600 dark:text-brand-400" />
        </DropdownMenu.ItemIndicator>
      </span>
      <span className="flex-1">{children}</span>
    </DropdownMenu.CheckboxItem>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-slate-200 dark:bg-slate-700" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Label className="px-2.5 pt-2 pb-1 text-xs font-medium text-slate-500">
      {children}
    </DropdownMenu.Label>
  );
}
